import {
  Button,
  Grid,
  MenuItem,
  Paper,
  TableContainer,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import BreadcrumbsComponent from "../../../Components/BreadCrumbs";
import { GetCity, GetState } from "react-country-state-city";
import LeftSideNav from "../../../Components/LeftSideNav";
import TopNavBar from "../../../Components/TopNavbar";
import Fonts from "../../../Config/Fonts";
import Strings from "../../../Config/Strings";
import { updateUserAPI } from "../../../Store/Action/UserAction";
import Colors from "../../../theme/Colors";
import CustomButton from "../../../Components/CustomButton";
import Loader from "../../../Components/Loader";
import { yupResolver } from "@hookform/resolvers/yup";
import { userSchema } from "../../../Services/ValidationSchema";

const UpdateUser = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = location?.state || {};

  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [stateId, setStateId] = useState(0);
  const [cityId, setCityId] = useState(0);
  const countryId = 101; // India is selected by default
  const [roleList, setRoleList] = useState([]); // State for roles
  const [selectedRoleId, setSelectedRoleId] = useState(""); // State for selected role
  const [showGSTFields, setShowGSTFields] = useState(true);
  const [showCompanyDocFields, setShowCompanyDocFields] = useState(false);
  const [selectedOption, setSelectedOption] = useState("yes");

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(userSchema),
    defaultValues: {
      firstName: user?.user_first_name,
      lastName: user?.user_last_name,
      userEmail: user?.user_email,
      userMobileNo: user?.user_mobile_number,
      userAddress: user?.user_address,
      shopName: user?.user_shop_name,
      state: user?.user_state,
      city: user?.user_state,
      role: null,
      gstOption: "",
      GSTNo: "",
      CompanyDoc: "",
      panCardNo: "",
      pinCode: "",
    },
    mode: "onBlur",
  });

  const handleGSTOptionChange = (option) => {
    setSelectedOption(option);
    if (option === "yes") {
      setShowGSTFields(true);
      setShowCompanyDocFields(false);
    } else {
      setShowGSTFields(false);
      setShowCompanyDocFields(true);
    }
  };

  const renderRequiredAsterisk = useCallback(
    () => <span style={requiredAsteriskStyle}>*</span>,
    []
  );

  useEffect(() => {
    if (countryId) {
      GetState(countryId).then((result) => {
        setStateList(result);
      });
    }
  }, [countryId]);

  const handleStateChange = useCallback((e) => {
    const selectedStateId = parseInt(e.target.value);
    setStateId(selectedStateId);
    setCityId(0);
    GetCity(countryId, selectedStateId).then((result) => {
      setCityList(result);
    });
  }, []);

  const handleCityChange = useCallback((e) => {
    const selectedCityId = parseInt(e.target.value);
    setCityId(selectedCityId);
  }, []);

  const handleRoleChange = useCallback((e) => {
    setSelectedRoleId(e.target.value);
  }, []);

  useEffect(() => {
    if (user && user.user_first_name) {
      setBreadcrumbs([
        { key: "1", href: "/", text: "Customers" },
        {
          key: "2",
          href: "/userList/edit",
          text: `Edit User`,
        },
      ]);
    }
  }, [user]);

  if (!user) {
    return <div>No User details available.</div>;
  }

  const onSubmit = async (data) => {
    const updateUserData = {
      user_first_name: data?.firstName,
      user_last_name: data?.lastName,
      user_email: data?.userAddress,
      user_address: data?.userEmail,
    };
    setLoading(true);
    try {
      await dispatch(
        updateUserAPI(updateUserData, (isSuccess, response) => {
          setLoading(false);
          if (isSuccess?.status === true) {
            toast.success(
              response?.data?.message || "User Updated Successfully"
            );
            navigate("/userList");
          } else {
            const errorMessage =
              response?.error?.message || Strings.somethingWentWrongError;
            toast.error(errorMessage);
          }
        })
      );
    } catch (error) {
      toast.error(Strings.somethingWentWrongError);
    }
  };

  return (
    <div style={mainContainer}>
      <LeftSideNav />

      <div style={leftNavContainer}>
        <div style={topBar}>
          <TopNavBar leftSideText={'Edit Customer'}/>
          <BreadcrumbsComponent breadcrumbs={breadcrumbs} />
        </div>

        <TableContainer component={Paper} style={tableContainer}>
          <form onSubmit={handleSubmit(onSubmit)} style={formStyle}>
            <Grid container spacing={2}>
              {/* First Name, Last Name, Shop Name */}
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle1" style={inputLabelFontStyle}>
                    First Name {renderRequiredAsterisk()}
                  </Typography>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder="Enter First Name"
                        variant="outlined"
                        fullWidth
                        error={!!errors.firstName}
                        helperText={errors.firstName?.message}
                        InputProps={{ style: inputTextStyle }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle1" style={inputLabelFontStyle}>
                    Last Name {renderRequiredAsterisk()}
                  </Typography>
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder="Enter Last Name"
                        variant="outlined"
                        fullWidth
                        error={!!errors.lastName}
                        helperText={errors.lastName?.message}
                        InputProps={{ style: inputTextStyle }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle1" style={inputLabelFontStyle}>
                    Shop Name {renderRequiredAsterisk()}
                  </Typography>
                  <Controller
                    name="shopName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder="Enter Shop Name"
                        variant="outlined"
                        fullWidth
                        error={!!errors.shopName}
                        helperText={errors.shopName?.message}
                        InputProps={{ style: inputTextStyle }}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {/* Mobile No, Alternate Mobile No */}
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" style={inputLabelFontStyle}>
                    Enter Mobile Number {renderRequiredAsterisk()}
                  </Typography>
                  <Controller
                    name="userMobileNo"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder="Enter Mobile Number"
                        variant="outlined"
                        fullWidth
                        error={!!errors.userMobileNo}
                        helperText={errors.userMobileNo?.message}
                        InputProps={{ style: inputTextStyle }}
                        inputProps={{ maxLength: 10 }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" style={inputLabelFontStyle}>
                    Enter Alternate Mobile Number
                  </Typography>
                  <Controller
                    name="alternateMobileNo"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder="Enter Alternate Mobile Number"
                        variant="outlined"
                        fullWidth
                        InputProps={{ style: inputTextStyle }}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {/* Email, PAN No */}
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" style={inputLabelFontStyle}>
                    Enter Email Address {renderRequiredAsterisk()}
                  </Typography>
                  <Controller
                    name="userEmail"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder="Enter email address"
                        variant="outlined"
                        fullWidth
                        error={!!errors.userEmail}
                        helperText={errors.userEmail?.message}
                        InputProps={{ style: inputTextStyle }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" style={inputLabelFontStyle}>
                    Enter PAN Number {renderRequiredAsterisk()}
                  </Typography>
                  <Controller
                    name="panCardNo"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder="Enter Pan Number"
                        variant="outlined"
                        fullWidth
                        error={!!errors.panCardNo}
                        helperText={errors.panCardNo?.message}
                        InputProps={{ style: inputTextStyle }}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {/* Role, Address */}
              <Grid
                container
                item
                xs={12}
                spacing={2}
                style={{ width: "100%" }}
              >
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle1" style={inputLabelFontStyle}>
                    Role
                  </Typography>
                  <Controller
                    name="role"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        variant="outlined"
                        value={selectedRoleId}
                        onChange={handleRoleChange}
                        error={!!errors.role}
                        helperText={errors.role?.message}
                        style={inputTextStyle} // Apply styling here
                      >
                        {roleList?.map((item) => (
                          <MenuItem
                            key={item?.user_role_id}
                            value={item?.user_role_id}
                          >
                            {item?.user_role_name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle1" style={inputLabelFontStyle}>
                    Address {renderRequiredAsterisk()}
                  </Typography>
                  <Controller
                    name="userAddress"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder="Address"
                        variant="outlined"
                        error={!!errors.userAddress}
                        helperText={errors.userAddress?.message}
                        style={inputTextStyle} // Apply styling here
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle1" style={inputLabelFontStyle}>
                    State {renderRequiredAsterisk()}
                  </Typography>
                  <Controller
                    name="state"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        variant="outlined"
                        value={stateId}
                        onChange={handleStateChange}
                        error={!!errors.state}
                        helperText={errors.state?.message}
                        style={inputTextStyle} // Apply styling here
                      >
                        {stateList.map((state) => (
                          <MenuItem key={state.id} value={state.id}>
                            {state.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle1" style={inputLabelFontStyle}>
                    City {renderRequiredAsterisk()}
                  </Typography>
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        variant="outlined"
                        value={cityId}
                        onChange={(e) => {
                          handleCityChange(e);
                        }}
                        error={!!errors.city}
                        helperText={errors.city?.message}
                        style={inputTextStyle} // Apply styling here
                        SelectProps={{ displayEmpty: true }} // Allow empty value
                      >
                        {cityList.map((city) => (
                          <MenuItem key={city.id} value={city.id}>
                            {city.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
              </Grid>

              {/* GST Yes/No */}
              <Grid
                container
                item
                xs={20}
                spacing={2}
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <Grid item xs={6}>
                  <Typography variant="subtitle1" style={inputLabelFontStyle}>
                    Are you a GST Holder? {renderRequiredAsterisk()}
                  </Typography>
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      onClick={() => handleGSTOptionChange("yes")}
                      style={{
                        width: "50%",
                        padding: 16,
                        border: `1px solid ${
                          selectedOption === "yes"
                            ? Colors.adminPrimaryColor
                            : "#363740"
                        }`,
                        backgroundColor:
                          selectedOption === "yes"
                            ? Colors.adminPrimaryColor
                            : Colors.white,
                        borderRadius: 4,
                        cursor: "pointer",
                        textAlign: "center",
                        marginRight: "10px",
                      }}
                    >
                      <p
                        style={{
                          color:
                            selectedOption === "yes"
                              ? Colors.white
                              : Colors.black,
                          fontSize: 16,
                          fontWeight: "600",
                          fontFamily: Fonts.Poppins,
                        }}
                      >
                        Yes
                      </p>
                    </div>

                    <div
                      onClick={() => handleGSTOptionChange("no")}
                      style={{
                        width: "50%",
                        padding: 16,
                        border: `1px solid ${
                          selectedOption === "no"
                            ? Colors.adminPrimaryColor
                            : "#363740"
                        }`,
                        backgroundColor:
                          selectedOption === "no"
                            ? Colors.adminPrimaryColor
                            : Colors.white,
                        borderRadius: 4,
                        cursor: "pointer",
                        textAlign: "center",
                      }}
                    >
                      <p
                        style={{
                          color:
                            selectedOption === "no"
                              ? Colors.white
                              : Colors.black,
                          fontSize: 16,
                          fontWeight: "600",
                          fontFamily: Fonts.Poppins,
                        }}
                      >
                        No
                      </p>
                    </div>
                  </div>
                </Grid>

                {showGSTFields && (
                  <Grid container item xs={6} spacing={2}>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        style={inputLabelFontStyle}
                      >
                        GST Number {renderRequiredAsterisk()}
                      </Typography>
                      <Controller
                        name="GSTNo"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            placeholder="GST Number"
                            variant="outlined"
                            fullWidth
                            error={!!errors.GSTNo}
                            helperText={errors.GSTNo?.message}
                            InputProps={{ style: inputTextStyle }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                )}

                {showCompanyDocFields && (
                  <Grid container item xs={6} spacing={2}>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        style={inputLabelFontStyle}
                      >
                        Company Document Number {renderRequiredAsterisk()}
                      </Typography>
                      <Controller
                        name="CompanyDoc"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            placeholder="Company Document Number"
                            variant="outlined"
                            fullWidth
                            error={!!errors.CompanyDoc}
                            helperText={errors.CompanyDoc?.message}
                            InputProps={{ style: inputTextStyle }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>

              {/* Save Button */}
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12}>
                  <CustomButton label={"Save"} />
                </Grid>
              </Grid>
            </Grid>
          </form>
        </TableContainer>
      </div>

      <Loader loading={loading} />
    </div>
  );
};

const mainContainer = {
  display: "grid",
  gridTemplateColumns: "20% 80%",
  height: "100vh",
  width: "100vw",
  overflowX: "hidden",
  overflowY: "hidden",
};

const leftNavContainer = {
  display: "grid",
  gridTemplateRows: "15% 80%",
  backgroundColor: "#F0F5FF",
};

const topBar = {
  backgroundColor: "white",
  height: "7vh",
  width: "100%",
};

const tableContainer = {
  width: "95%",
  border: "1px solid #ccc",
  borderRadius: "15px",
  boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
  marginTop: "15px",
  paddingRight: "20px",
  marginLeft: "25px",
};

const formStyle = {
  padding: "20px",
};

const inputLabelFontStyle = {
  textAlign: "left",
  fontSize: 16,
  fontWeight: "500",
  fontFamily: Fonts.Poppins,
  color: Colors.headingBlackColor,
};

const inputTextStyle = {
  width: "100%",
  textAlign: "left",
  fontSize: 14,
  fontWeight: "400",
  fontFamily: Fonts.Poppins,
  color: Colors.paginationTitle,
};

const requiredAsteriskStyle = {
  color: "red",
};

export default UpdateUser;
